import React, { useEffect } from 'react'
import { bool, func, shape, string, arrayOf, number } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { useAtSize, Button, useOverlay, DialogOverlay, DialogOverlayContent, HeaderBlock } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import Couples from './Couples'
import AncestorCard from './AncestorCard'
import { useMiniTree } from './hooks/hooks'
import { useTreeDispatch } from './state/TreeStore'
import useDSApplicationMode from '../shared/hooks/useDSApplicationMode'

const coupleContainerCss = css`
  display: flex;
  justify-content: center;
  width: 100%;
`

const treeContainerCss = css`
  display: flex;
  flex-wrap: wrap;
  height: fit-content;
  justify-content: space-around;
  align-content: flex-start;
  position: relative;
  + button {
    display: block;
    margin: 0 auto;
    width: 150px;
  }
  + a {
    display: block;
    margin: 0 auto;
    width: 150px;
  }
`

export default function AncestryTreeAam({ setIsTreeShowing, overlay, aamState, aamDispatch }) {
  const user = useUser()
  const state = useMiniTree()
  const treeDispatch = useTreeDispatch()
  const [t] = useTranslation()
  const atSize = useAtSize()
  const atfOverlay = useOverlay()
  const isWebMode = useDSApplicationMode() === 'WEB'
  const isMissingFather = !state.userTree[2].name && !state.userTree[2].surname
  const isMissingMother = !state.userTree[3].name && !state.userTree[3].surname

  const userContainerCSS = css`
    display: flex;
    justify-content: center;
    width: 100%;
    margin: 32px 0;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.1);
    ${atSize({ default: 'width: calc(100vw / 5.25)', md: 'width: 120px' })};
    ${atSize({ default: 'height: calc(100vw / 3)', md: 'height: 175px' })};
  `

  const btnWrapperStyle = css`
    ${atSize({ default: 'width: 100%', md: 'width: 60%' })};
    margin: 32px auto;
    text-align: center;
  `

  const grandparentSpacerCss = css`
    ${atSize({ default: 'width: 20px', md: 'width: 80px' })};
  `

  // This will insert the guest user information the first time they visit
  // add tree flow if they decide to proceed and not save information.
  useEffect(() => {
    if (aamState?.isGuestUser) {
      if (aamState?.guestTree.length !== 0) {
        aamState?.guestTree.forEach((person, index) => {
          if (index === 0) {
            const { firstName, lastName, birthYear, name, surname, genderType } = aamState?.guestTree[0]
            const guestUser = {
              atn: 1,
              name: firstName ?? name,
              surname: lastName ?? surname,
              dob: birthYear,
              displayName: `${firstName ?? name} ${lastName ?? surname}`,
              genderType,
            }
            treeDispatch({
              type: 'COMMIT_ANCESTOR',
              payload: {
                person: guestUser,
              },
            })
          } else {
            treeDispatch({
              type: 'COMMIT_ANCESTOR',
              payload: {
                person,
              },
            })
          }
        })
      }
    }
  }, [aamState.guestTree, aamState.isGuestUser, treeDispatch])

  useEffect(() => {
    if (!aamState?.atfGuestOptionSelected && aamState?.isGuestUser) {
      atfOverlay.open()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleClose = () => {
    aamDispatch({
      type: 'UPDATE_ATF_OPTION',
      payload: {
        atfGuestOptionSelected: true,
      },
    })
    atfOverlay.close()
  }

  return (
    <>
      <DialogOverlay {...atfOverlay} fullscreen={false}>
        <DialogOverlayContent>
          <HeaderBlock
            size="xs"
            heading={t('atf.ancestor-tree.modal.header', 'Would you like to save this information')}
          />
          <p>
            {t(
              'atf.ancestor-tree.modal.disclaimer',
              'If you would like FamilySearch to save this information you will need to sign-in or create an account. Otherwise this information will be removed when you exit the browser.'
            )}
          </p>
          <div css={btnWrapperStyle}>
            {isWebMode && (
              <Button emphasis="high" to={`/register?returnUrl=${encodeURI('/discovery/about')}`} external fullWidth>
                {t('atf.ancestor-tree.modal.btnOne', 'Create A Free Account')}
              </Button>
            )}
            {isWebMode && (
              <Button emphasis="high" to="/auth/familysearch/login?returnUrl=/discovery/about" external fullWidth>
                {t('atf.ancestor-tree.modal.btnTwo', 'Sign In')}
              </Button>
            )}
            <Button emphasis="high" onClick={handleClose} fullWidth>
              {t('atf.ancestor-tree.modal.btnThree', 'Continue And Dont Save')}
            </Button>
          </div>
        </DialogOverlayContent>
      </DialogOverlay>
      <div css={treeContainerCss}>
        <div css={coupleContainerCss}>
          <Couples
            side="left"
            which="grandparents"
            leftAncestor={state.userTree[4]}
            rightAncestor={state.userTree[5]}
            disabled={isMissingFather && user.signedIn}
            setIsTreeShowing={setIsTreeShowing}
          />
          <div css={grandparentSpacerCss} />
          <Couples
            side="right"
            which="grandparents"
            leftAncestor={state.userTree[6]}
            rightAncestor={state.userTree[7]}
            disabled={isMissingMother && user.signedIn}
            setIsTreeShowing={setIsTreeShowing}
          />
        </div>
        <div css={coupleContainerCss}>
          <Couples
            which="parents"
            leftAncestor={state.userTree[2]}
            rightAncestor={state.userTree[3]}
            setIsTreeShowing={setIsTreeShowing}
          />
        </div>
        <div css={userContainerCSS}>
          <AncestorCard personData={state.userTree[1]} setIsTreeShowing={setIsTreeShowing} />
        </div>
      </div>
      <Button emphasis="high" onClick={overlay.handleClick}>
        {t('poh.takeaway.submit', 'Submit')}
      </Button>
    </>
  )
}

AncestryTreeAam.propTypes = {
  setIsTreeShowing: func,
  overlay: shape({
    isOpen: bool.isRequired,
    open: func.isRequired,
    close: func.isRequired,
  }).isRequired,
  aamState: shape({
    atfGuestOptionSelected: bool,
    countryCode: string,
    firstName: string,
    fullName: string,
    guestTree: arrayOf(shape({})),
    initialTileDataRequested: bool,
    isAddTreeFlowShowing: bool,
    isGuestUser: bool,
    isLoading: bool,
    lastName: string,
    options: shape,
    tileData: arrayOf(shape({})),
    tree: arrayOf(shape({})),
    year: number,
  }),
  aamDispatch: func,
}
